import { PlusOutlined } from '@ant-design/icons';
import { useCustom, useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  PATHWAY_LIBRARY_FIELDS,
  type PathwayLibrary,
  type PathwayRow,
} from './model';
import { usePathwayCommand } from './usePathwayCommand';

/**
 * E34 · Pathways — "Practice — Pathways (library)" (cc-homework, chapter "Pathways — Define & enroll
 * (portal, E34)", screen e34e0004-0001-4000-8000-000000000005): the practice's pathways — name, steps,
 * visibility, version, enrolled · completed. A copy names its original; a public course shows its review
 * state (in review · published · not published, CopingCard's reason on hover); a copy of a CopingCard
 * program shows the access rule it keeps; the header shows the practice's course capabilities. With
 * "Practice — Pathways — Retire (section)" (e34e0004-…-0012): Retire → confirm with a reason → RetirePathway.
 *
 * Behavior lines implemented here: gesture Duplicate (every row) → the Builder opens on a copy (" (copy)",
 * copiedFrom set), i18n pathways.library.duplicate; gesture Edit / Retire, i18n pathways.library.* /
 * pathways.retire.*; format the review chip, i18n pathways.library.review.*; format the capability chips in
 * the header, i18n pathways.library.capabilities; nav Outcomes → "Practice — Pathways — Outcomes",
 * i18n pathways.library.outcomes. A retired pathway stays listed, greyed; the pickers drop it.
 */
export const PathwayLibraryPage = () => {
  const t = useTranslate();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const { run } = usePathwayCommand();
  const [retiring, setRetiring] = useState<PathwayRow | null>(null);
  const [reason, setReason] = useState('');
  const [retireProblem, setRetireProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, isFetching, isError, refetch } = useCustom<PathwayLibrary>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'pathways',
      fields: PATHWAY_LIBRARY_FIELDS,
      variables: {
        includeRetired: { name: 'includeRetired', type: 'Boolean', value: true },
      },
    },
  });
  const rows = useMemo(() => data?.data?.pathways ?? [], [data]);
  const capabilities = data?.data?.capabilities ?? [];

  const dateLocale = i18n.languages[0]?.startsWith('ru') ? ru : enUS;
  const when = (iso: string | null | undefined) =>
    iso ? format(parseISO(iso), 'd MMM yyyy', { locale: dateLocale }) : '—';

  const retire = async () => {
    if (!retiring) return;
    setBusy(true);
    const r = await run(
      'retirePathway',
      { pathwayId: retiring.pathwayId, reason: reason.trim() || null },
      t('pathways.retire.failed'),
    );
    setBusy(false);
    if (!r.ok) {
      setRetireProblem(r.message);
      return;
    }
    message.success(t('pathways.retire.done'));
    setRetiring(null);
    refetch();
  };

  const reviewChip = (r: PathwayRow) => {
    if (!r.reviewStatus) return null;
    const color =
      r.reviewStatus === 'published' ? 'green' : r.reviewStatus === 'notPublished' ? 'red' : 'gold';
    const chip = (
      <Tag color={color} data-testid={`pathway-row-review-${r.pathwayId}`}>
        {t(`pathways.library.review.${r.reviewStatus}`)}
      </Tag>
    );
    return r.reason ? <Tooltip title={r.reason}>{chip}</Tooltip> : chip;
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="pathway-library">
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Typography.Title level={3} style={{ marginBottom: 0 }}>
            {t('pathways.library.title')}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 4 }}>
            {t('pathways.library.intro')}
          </Typography.Paragraph>
          <Space size={4} wrap data-testid="pathway-library-capabilities">
            <Typography.Text type="secondary">{t('pathways.library.capabilities')}</Typography.Text>
            {capabilities.length ? (
              capabilities.map((c) => (
                <Tag key={c} color="purple" data-testid={`pathway-capability-${c}`}>
                  {t(`pathways.capability.${c}`)}
                </Tag>
              ))
            ) : (
              <Tag data-testid="pathway-capability-none">{t('pathways.library.noCapabilities')}</Tag>
            )}
          </Space>
        </div>
        <Space>
          <Link to="/pathways/outcomes" data-testid="pathway-library-outcomes">
            <Button>{t('pathways.library.outcomes')}</Button>
          </Link>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/pathways/new')}
            data-testid="pathway-library-new"
          >
            {t('pathways.library.new')}
          </Button>
        </Space>
      </Space>

      {isError ? <Alert type="error" showIcon message={t('pathways.library.loadFailed')} /> : null}

      <Table<PathwayRow>
        rowKey="pathwayId"
        loading={isFetching}
        dataSource={rows}
        pagination={{ pageSize: 25, hideOnSinglePage: true }}
        locale={{ emptyText: t('pathways.library.empty') }}
        onRow={(r) =>
          ({
            'data-testid': `pathway-row-${r.pathwayId}`,
            'data-status': r.status,
            'style': r.status === 'retired' ? { opacity: 0.55 } : undefined,
          } as never)
        }
        columns={[
          {
            title: t('pathways.library.column.name'),
            render: (_, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong data-testid={`pathway-row-name-${r.pathwayId}`}>
                  {r.name}
                </Typography.Text>
                {r.copiedFrom ? (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12 }}
                    data-testid={`pathway-row-copy-${r.pathwayId}`}
                  >
                    {t('pathways.library.copyOf', { name: r.copiedFromName ?? r.copiedFrom })}
                  </Typography.Text>
                ) : null}
                {r.accessRule ? (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12 }}
                    data-testid={`pathway-row-access-${r.pathwayId}`}
                  >
                    {t(`pathways.library.access.${r.accessRule}`)}
                  </Typography.Text>
                ) : null}
                {r.reviewStatus === 'notPublished' && r.reason ? (
                  <Typography.Text
                    type="danger"
                    style={{ fontSize: 12 }}
                    data-testid={`pathway-row-review-reason-${r.pathwayId}`}
                  >
                    {t('pathways.library.review.notPublishedReason', { reason: r.reason })}
                  </Typography.Text>
                ) : null}
              </Space>
            ),
          },
          {
            title: t('pathways.library.column.steps'),
            width: 80,
            render: (_, r) => <span data-testid={`pathway-row-steps-${r.pathwayId}`}>{r.stepsCount}</span>,
          },
          {
            title: t('pathways.library.column.visibility'),
            width: 170,
            render: (_, r) => (
              <Space size={4} wrap>
                <Tag data-testid={`pathway-row-visibility-${r.pathwayId}`}>
                  {r.status === 'retired'
                    ? t('pathways.library.retired')
                    : t(`pathways.visibility.${r.visibility}`)}
                </Tag>
                {reviewChip(r)}
              </Space>
            ),
          },
          {
            title: t('pathways.library.column.version'),
            width: 90,
            render: (_, r) => (
              <span data-testid={`pathway-row-version-${r.pathwayId}`}>
                {t('pathways.library.version', { version: r.version })}
              </span>
            ),
          },
          {
            title: t('pathways.library.column.enrolled'),
            width: 150,
            render: (_, r) => (
              <span data-testid={`pathway-row-enrolled-${r.pathwayId}`}>
                {r.enrolledCount} · {r.completedCount}
              </span>
            ),
          },
          {
            title: t('pathways.library.column.updated'),
            width: 120,
            render: (_, r) => when(r.occurredAt),
          },
          {
            title: '',
            key: 'actions',
            width: 250,
            render: (_, r) =>
              r.status === 'retired' ? (
                <Button
                  size="small"
                  onClick={() => navigate(`/pathways/new?from=${encodeURIComponent(r.pathwayId)}`)}
                  data-testid={`pathway-row-duplicate-${r.pathwayId}`}
                >
                  {t('pathways.library.duplicate')}
                </Button>
              ) : (
                <Space size={4} wrap>
                  <Button
                    size="small"
                    onClick={() => navigate(`/pathways/${encodeURIComponent(r.pathwayId)}/edit`)}
                    data-testid={`pathway-row-edit-${r.pathwayId}`}
                  >
                    {t('pathways.library.edit')}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => navigate(`/pathways/new?from=${encodeURIComponent(r.pathwayId)}`)}
                    data-testid={`pathway-row-duplicate-${r.pathwayId}`}
                  >
                    {t('pathways.library.duplicate')}
                  </Button>
                  <Button
                    size="small"
                    danger
                    onClick={() => {
                      setRetiring(r);
                      setReason('');
                      setRetireProblem(null);
                    }}
                    data-testid={`pathway-row-retire-${r.pathwayId}`}
                  >
                    {t('pathways.retire.action')}
                  </Button>
                </Space>
              ),
          },
        ]}
      />

      <Modal
        open={!!retiring}
        title={t('pathways.retire.title')}
        okText={t('pathways.retire.confirm')}
        cancelText={t('pathways.retire.cancel')}
        okButtonProps={{ danger: true, loading: busy, 'data-testid': 'pathway-retire-confirm' } as never}
        onOk={retire}
        onCancel={() => setRetiring(null)}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} data-testid="pathway-retire-dialog">
          <Typography.Paragraph>
            {t('pathways.retire.body', {
              name: retiring?.name ?? '',
              count: retiring?.runningCount ?? 0,
            })}
          </Typography.Paragraph>
          <Input.TextArea
            value={reason}
            maxLength={1000}
            autoSize={{ minRows: 2, maxRows: 5 }}
            placeholder={t('pathways.retire.reasonPlaceholder')}
            onChange={(e) => setReason(e.target.value)}
            data-testid="pathway-retire-reason"
          />
          {retireProblem ? (
            <Alert type="error" showIcon message={retireProblem} data-testid="pathway-retire-error" />
          ) : null}
        </Space>
      </Modal>
    </Space>
  );
};
